import { ReactElement } from 'react'
import type {
  CanvasPosition,
  EndpointDisplayInfo,
  LinkStatus,
} from '../../types/infrastructure-map'
import { BusTopology, LinkLine, SummaryTile, TreeTopology } from './canvas'
import { buildBusTopologyData, buildTreeTopologyData } from './infrastructure-map-canvas.helpers'

const CANVAS_SIZE = { width: 4000, height: 3000 }
const SUMMARY_TILE_Y_OFFSET = 80

export interface LinkToRender {
  id: string
  sourcePosition: CanvasPosition
  targetPosition: CanvasPosition
  status: LinkStatus
  hasOpenQuestion: boolean
}

export interface SvgLayerProps {
  mapStyle: string
  busTopologyData: ReturnType<typeof buildBusTopologyData>
  treeTopologyData: ReturnType<typeof buildTreeTopologyData>
  linksToRender: LinkToRender[]
  summaryTiles: Map<
    string,
    {
      parentEndpointId: string
      directChildCount: number
      totalDescendantCount: number
      isRecursive: boolean
      hiddenEndpointIds: string[]
    }
  >
  displayInfo: Map<string, EndpointDisplayInfo>
  collapsedPositions: Map<string, CanvasPosition>
  editLinkId: string | null
  toggleCollapse: (id: string) => Promise<void>
  toggleCollapseRecursive: (id: string) => void
  onLinkContextMenu: (id: string, e: React.MouseEvent) => void
}

interface BusTopologyLayerProps {
  busTopologyData: ReturnType<typeof buildBusTopologyData>
  toggleCollapse: (id: string) => Promise<void>
  toggleCollapseRecursive: (id: string) => void
}

function BusTopologyLayer({
  busTopologyData,
  toggleCollapse,
  toggleCollapseRecursive,
}: BusTopologyLayerProps): ReactElement {
  return (
    <>
      {busTopologyData.map((bus) => (
        <BusTopologyGroupRender
          key={`bus-group-${String(bus.lanGroupId)}`}
          group={bus}
          toggleCollapse={toggleCollapse}
          toggleCollapseRecursive={toggleCollapseRecursive}
        />
      ))}
    </>
  )
}

interface TreeTopologyLayerProps {
  treeTopologyData: ReturnType<typeof buildTreeTopologyData>
  toggleCollapse: (id: string) => Promise<void>
  toggleCollapseRecursive: (id: string) => void
}

function TreeTopologyLayer({
  treeTopologyData,
  toggleCollapse,
  toggleCollapseRecursive,
}: TreeTopologyLayerProps): ReactElement {
  return (
    <>
      {treeTopologyData.map((tree) => (
        <TreeTopology
          key={`tree-${String(tree.lanGroupId)}`}
          nodes={tree.nodes}
          lanGroupId={tree.lanGroupId}
          onToggleCollapse={(...args: Parameters<typeof toggleCollapse>) => {
            void toggleCollapse(...args)
          }}
          onToggleCollapseRecursive={toggleCollapseRecursive}
        />
      ))}
    </>
  )
}

interface LinksLayerProps {
  links: LinkToRender[]
  editLinkId: string | null
  onLinkContextMenu: (id: string, e: React.MouseEvent) => void
  keyPrefix?: string
}

function LinksLayer({
  links,
  editLinkId,
  onLinkContextMenu,
  keyPrefix = '',
}: LinksLayerProps): ReactElement {
  return (
    <g style={{ pointerEvents: 'auto' }}>
      {links.map((link) => (
        <LinkLine
          key={`${keyPrefix}${link.id}`}
          sourcePosition={link.sourcePosition}
          targetPosition={link.targetPosition}
          status={link.status}
          hasOpenQuestion={link.hasOpenQuestion}
          isSelected={editLinkId === link.id}
          onContextMenu={(e) => {
            onLinkContextMenu(link.id, e)
          }}
        />
      ))}
    </g>
  )
}

interface SummaryTilesLayerProps {
  summaryTiles: SvgLayerProps['summaryTiles']
  displayInfo: Map<string, EndpointDisplayInfo>
  collapsedPositions: Map<string, CanvasPosition>
  toggleCollapse: (id: string) => Promise<void>
}

function SummaryTilesLayer({
  summaryTiles,
  displayInfo,
  collapsedPositions,
  toggleCollapse,
}: SummaryTilesLayerProps): ReactElement {
  return (
    <>
      {Array.from(summaryTiles.entries()).map(([parentId, data]) => {
        const parentInfo = displayInfo.get(parentId)
        if (!parentInfo?.position) return null
        if (data.directChildCount === 0) return null
        const parentPos = collapsedPositions.get(parentId) ?? parentInfo.position
        return (
          <SummaryTile
            key={`summary-${parentId}`}
            data={data}
            position={{ x: parentPos.x, y: parentPos.y + SUMMARY_TILE_Y_OFFSET }}
            onClick={() => {
              void toggleCollapse(parentId)
            }}
            hasExternalLinks={false}
          />
        )
      })}
    </>
  )
}

export function CanvasSvgLayer({
  mapStyle,
  busTopologyData,
  treeTopologyData,
  linksToRender,
  summaryTiles,
  displayInfo,
  collapsedPositions,
  editLinkId,
  toggleCollapse,
  toggleCollapseRecursive,
  onLinkContextMenu,
}: SvgLayerProps): ReactElement {
  const specialLinks = linksToRender.filter(
    (link) => link.status === 'open-question' || link.status === 'suspected'
  )
  const isBusOrTree = mapStyle === 'bus' || mapStyle === 'tree'
  return (
    <svg
      className="absolute top-0 left-0"
      width={CANVAS_SIZE.width}
      height={CANVAS_SIZE.height}
      style={{ overflow: 'visible', pointerEvents: 'none' }}
    >
      {mapStyle === 'bus' && (
        <BusTopologyLayer
          busTopologyData={busTopologyData}
          toggleCollapse={toggleCollapse}
          toggleCollapseRecursive={toggleCollapseRecursive}
        />
      )}
      {mapStyle === 'tree' && (
        <TreeTopologyLayer
          treeTopologyData={treeTopologyData}
          toggleCollapse={toggleCollapse}
          toggleCollapseRecursive={toggleCollapseRecursive}
        />
      )}
      {!isBusOrTree && (
        <LinksLayer
          links={linksToRender}
          editLinkId={editLinkId}
          onLinkContextMenu={onLinkContextMenu}
        />
      )}
      {isBusOrTree && (
        <LinksLayer
          links={specialLinks}
          editLinkId={editLinkId}
          onLinkContextMenu={onLinkContextMenu}
          keyPrefix="special-"
        />
      )}
      {mapStyle === 'tree' && (
        <SummaryTilesLayer
          summaryTiles={summaryTiles}
          displayInfo={displayInfo}
          collapsedPositions={collapsedPositions}
          toggleCollapse={toggleCollapse}
        />
      )}
    </svg>
  )
}

interface BusTopologyGroupRenderProps {
  group: ReturnType<typeof buildBusTopologyData>[number]
  toggleCollapse: (endpointId: string) => Promise<void>
  toggleCollapseRecursive: (endpointId: string) => void
}

function BusTopologyGroupRender({
  group,
  toggleCollapse,
  toggleCollapseRecursive,
}: BusTopologyGroupRenderProps): ReactElement {
  const hubNode = group.nodes.find((node) => node.isHub)
  return (
    <g>
      <BusTopology
        nodes={group.nodes}
        busY={group.busY}
        lanGroupId={group.lanGroupId}
        onToggleCollapse={(...args: Parameters<typeof toggleCollapse>) => {
          void toggleCollapse(...args)
        }}
        onToggleCollapseRecursive={toggleCollapseRecursive}
      />
      {group.collapsedCount > 0 && hubNode && (
        <SummaryTile
          data={{
            parentEndpointId: group.hubId,
            directChildCount: group.collapsedCount,
            totalDescendantCount: group.collapsedCount,
            isRecursive: false,
            hiddenEndpointIds: group.collapsedEndpointIds,
          }}
          position={{ x: hubNode.position.x + 100, y: group.busY }}
          onClick={() => {
            for (const endpointId of group.collapsedEndpointIds) void toggleCollapse(endpointId)
          }}
          hasExternalLinks={false}
        />
      )}
    </g>
  )
}
